import type { CaptureResult } from 'posthog-js/dist/module.no-external';
import { HTTP_STATUS_NOT_FOUND } from '@/common/constants/httpStatus.constants';
import { rootPath } from '@/common/utils/pathPrefix.util';
import { isPreloadErrorMessage } from '@/common/utils/preloadError.util';

type ExceptionItem = {
  type?: string;
  value?: string;
};

type ExceptionProperties = {
  $exception_list?: ExceptionItem[];
  error_code?: string;
  status_code?: number;
  request_url?: string;
};

type DenyPredicate = (event: CaptureResult, exception: ExceptionItem | null) => boolean;

export const EXCEPTION_DEDUPE_WINDOW_MS = 5000;

const CHUNK_PRELOAD_FAILURE_ERROR_CODE = 'chunk_preload_failure';

const getException = (event: CaptureResult): ExceptionItem | null => {
  const list = (event.properties as ExceptionProperties)?.$exception_list;
  return Array.isArray(list) && list.length > 0 ? list[0] : null;
};

const getErrorCode = (event: CaptureResult): string | undefined => (event.properties as ExceptionProperties)?.error_code;

const getStatusCode = (event: CaptureResult): number | undefined => (event.properties as ExceptionProperties)?.status_code;

// Telemetry records the URI as it was built: `rootDir` + path + query. Strip both ends so the
// patterns below match on agent-direct deployments too, where `rootDir` is a non-empty prefix.
const getRequestUrl = (event: CaptureResult): string => {
  const path = ((event.properties as ExceptionProperties)?.request_url ?? '').split('?')[0];
  return rootPath && path.startsWith(rootPath) ? path.slice(rootPath.length) : path;
};

// VSTOR-145463: an endpoint that answers 404 when an optional resource was never set, or
// when the service is not deployed on this cluster. Absence is the answer there, not a
// failure. Only on 404: a 400, 403 or 5xx on the very same URL is still a real failure
// and still reaches the error stream.
const expectedAbsence = (pattern: RegExp): DenyPredicate => (e) => (
  getStatusCode(e) === HTTP_STATUS_NOT_FOUND && pattern.test(getRequestUrl(e))
);

// The backend sends no err_code with this one, so the message is the only thing that
// identifies it. It is English today (we send no Accept-Language and NotFoundError keeps
// the backend body verbatim) but this predicate is the one thing here that a localised
// backend message would silently break -- see the err_code request in VSTOR-145463.
const SERVICE_NOT_INSTALLED = 'Service is not installed';

const DENY_PREDICATES: DenyPredicate[] = [
  (_, ex) => ex?.type === 'CancellationError',
  (_, ex) => ex?.value === 'Cancel action',
  (_, ex) => ex?.type === 'AbortError',
  (_, ex) => ex?.value === 'The operation was aborted.',
  (_, ex) => /could\s*not\s*be\s*found/i.test(ex?.value ?? ''),
  (e) => ['s3_not_found', 's3_disabled', 's3_user_disabled'].includes(getErrorCode(e) ?? ''),
  // Every entry is one measured in PostHog over 30 days; endpoints whose 404 was never
  // observed are deliberately left out, because each entry is also a blind spot.
  // The domain segment is `[^/]*`, not `[^/]+`, because the system-level self-service theme
  // page reads the sheet before a domain is chosen: Theme.vue defaults `domainName` to '' and
  // its immediate watcher fetches `/api/v2/domains//props/theme`, using the 404 to fall
  // through to the general theme.
  expectedAbsence(/^\/api\/v2\/domains\/[^/]*\/props\/[^/]+$/), // optional property sheets
  expectedAbsence(/^\/api\/v2\/features$/), // remote feature flags, absent on 6.3-7.2 backends (VSTOR-146910)
  expectedAbsence(/^\/api\/v2\/mpfs\/cluster$/), // file storage not installed
  // Every request to a service that is not deployed answers this, so there is no URL list
  // to match on. Reported both by the adapter and, where a caller leaves the rejection
  // unhandled, a second time by exception autocapture, which carries no status or URL.
  (_, ex) => ex?.value === SERVICE_NOT_INSTALLED,
  // Benign browser noise: no user-visible effect and no recoverable action. Matching the
  // phrase, not the bare word, keeps real ResizeObserver bugs (a bad observe() argument)
  // in the error stream.
  (_, ex) => /ResizeObserver loop/i.test(ex?.value ?? ''),
];

const isChunkPreloadFailure = (exception: ExceptionItem | null): boolean => {
  const exceptionMessage = exception?.value;
  return typeof exceptionMessage === 'string' && isPreloadErrorMessage(exceptionMessage);
};

const tagChunkPreloadFailure = (event: CaptureResult): void => {
  const properties = event.properties as ExceptionProperties;
  if (!properties.error_code) {
    properties.error_code = CHUNK_PRELOAD_FAILURE_ERROR_CODE;
  }
};

const buildExceptionSignature = (exception: ExceptionItem): string => `${exception.type ?? ''}::${exception.value ?? ''}`;

const lastEmittedAtBySignature = new Map<string, number>();

export const getDedupeCacheSize = (): number => lastEmittedAtBySignature.size;

const isDuplicateWithinDedupeWindow = (signature: string): boolean => {
  const capturedAt = Date.now();
  const lastEmittedAt = lastEmittedAtBySignature.get(signature);
  const isDuplicate = typeof lastEmittedAt === 'number'
    && capturedAt - lastEmittedAt < EXCEPTION_DEDUPE_WINDOW_MS;

  if (!isDuplicate) {
    lastEmittedAtBySignature.forEach((emittedAt, knownSignature) => {
      if (capturedAt - emittedAt >= EXCEPTION_DEDUPE_WINDOW_MS) {
        lastEmittedAtBySignature.delete(knownSignature);
      }
    });
    lastEmittedAtBySignature.set(signature, capturedAt);
  }

  return isDuplicate;
};

const shouldFilterError = (event: CaptureResult | null): boolean => {
  if (!event || event.event !== '$exception') {
    return false;
  }

  const exception = getException(event);

  if (DENY_PREDICATES.some(predicate => predicate(event, exception))) {
    return true;
  }

  if (!exception) {
    return false;
  }

  if (isChunkPreloadFailure(exception)) {
    tagChunkPreloadFailure(event);
  }

  return false;
};

/**
 * Stateful companion to `shouldFilterError`, deliberately kept separate: `before_send` runs the
 * filter on every captured event AND again on every buffered event it replays, so a window check
 * folded into the filter would see one incident twice and drop the replay. This must therefore be
 * called exactly once per event, on the path where the event is actually sent.
 */
export const isDuplicateChunkPreloadFailure = (event: CaptureResult | null): boolean => {
  if (!event || event.event !== '$exception') {
    return false;
  }

  const exception = getException(event);

  if (!exception || !isChunkPreloadFailure(exception)) {
    return false;
  }

  return isDuplicateWithinDedupeWindow(buildExceptionSignature(exception));
};

export default shouldFilterError;
